import { podeVerIaDriven } from '@modules/global/utils/chat-ia-widget-visivel'
import { FEATURES, SEARCH_GROUPS } from '@constants/Text'
import { lazyLoadView, lazyLoadViewOnce } from '@utils/LazyLoad'
import { isWelcomeDismissed } from '@modules/design/utils/welcome-storage'
import {
  ROTA_IMERSIVA,
  ROTA_IMERSIVA_CONVERSA,
} from '@modules/aawz-intelligence-v2/views/chat-imersivo/constants'
import {
  ACOES_TEXT,
  APRENDIZADO_TEXT,
  CONHECIMENTO_TEXT,
  METRICAS_TEXT,
  SKILLS_TEXT,
} from '@modules/aawz-intelligence-v2/constants/Text'
// NICE 16 (review front it.7): as 2 rotas do Advisory Hub saíram para o
// irmão `routes.advisory.js` — este arquivo estava em 500/500, no teto do
// repo (`CLAUDE.md` raiz).
import advisoryRoutes from './routes.advisory'

/**
 * ★ UMA factory para as DUAS rotas da tela imersiva (`/home-ia` e
 * `/home-ia/c/:idConversa`) — e é obrigatório que seja a mesma.
 *
 * O vue-router resolve o componente POR RouteRecord (`def.cid === undefined`,
 * e uma factory nunca ganha `cid`), então duas declarações
 * `() => lazyLoadView(import(...))` chamam `lazyLoadView` duas vezes e cada
 * uma fabrica o seu `AsyncHandler` — o da rota nova **ainda não resolvido**.
 *
 * O primeiro render depois da navegação vira então um **placeholder de async**:
 * vnode de comentário, sem `tag` e sem `key`. `sameVnode` só perdoa placeholder
 * quando o vnode ANTIGO é o placeholder — aqui o antigo é um componente
 * montado, então o diff destrói antes de o render resolvido chegar. A
 * `meta.chaveDeMontagem` igual nas duas rotas não alcança esse caso: o
 * placeholder nem key carrega.
 *
 * ⚠️ O `Ctor` não diverge — medido, a tag é a MESMA dos dois lados. Quem
 * supõe o contrário tenta consertos que não funcionam.
 *
 * Medido em 20/09/2026 (spec-009): era isto que destruía o `<ViewChat>` no
 * primeiro envio, quando a conversa ganha id e a URL sai de `/home-ia` para
 * `/home-ia/c/<uuid>` — levando junto o anexo pendente
 * (`beforeDestroy` → `descartarFilaDeAnexos`). Trocar de conversa depois
 * (`/c/A` → `/c/B`) nunca remontou, porque aí o RouteRecord é o mesmo e já
 * está resolvido: é essa assimetria que denuncia o mecanismo.
 *
 * O `import()` segue dentro da factory — o chunk continua fora do bundle
 * inicial. Ver `@utils/LazyLoad::lazyLoadViewOnce` e
 * `src/router/lazy-load-view.remontagem.unit.js`.
 */
const telaImersivaDoChat = lazyLoadViewOnce(() =>
  import(
    '@modules/aawz-intelligence-v2/views/chat-imersivo/view-chat-imersivo.vue'
  )
)

// Gate único da área: qualquer usuário com a feature CHAT_IA. A exigência
// de gestor foi removida — o motor real do chat é o aawz-intelligence-v2
// (ex-Agente 360). Usa CHAT_IA (não IA_CRM_CHAT): a área não é mais uma
// aba do CRM, é um menu próprio — flag com nome de CRM confundia (revisão
// do Leandro no PR #1538).
export const checkPermission = ({ show }) => Boolean(show[FEATURES.CHAT_IA])

/**
 * Gate SÓ da visão imersiva (`/home-ia`), separado do `checkPermission` acima
 * porque ela tem flag própria (`home_ia`) e exige a navegação 2026 — a régua é
 * `podeVerIaDriven` (`global/utils/chat-ia-widget-visivel`), a mesma do menu,
 * do atalho e do guard da raiz; ler a flag aqui por conta própria mandaria o
 * usuário a uma rota que a outra régua recusa.
 *
 * ⚠️ `customPermission` recebe `{ user, show }` — `show` É o mapa de features
 * que o helper chama de `access`.
 */
export const checkImersivaPermission = ({ user, show }) =>
  podeVerIaDriven({ user, access: show })

// Chaves de API é gestão de credenciais de integração — ferramenta do
// PROFISSIONAL, nunca pro cliente final, mesmo com CHAT_IA ligada no
// escritório.
export const checkApiKeysPermission = (opts) => {
  const user = opts.user || {}
  if (user.IS_CLIENTE) return false
  return checkPermission(opts)
}

// spec-007-aprovacao-de-skills-por-risco ([T033]) — as 3 abas novas do
// módulo (texto em `Text.aprendizado.js`, [T021]). `aprendizado-ia` (timeline
// de publicações/reversões) e `skills-ia` (catálogo de skills) são
// GOVERNANÇA da IA do escritório — GESTOR only, mesmo padrão de
// `ai-framework-nav-bar.vue` (`isGestor`). Cliente final é bloqueado, mesmo
// precedente de `checkApiKeysPermission` acima: ferramenta de quem OPERA a
// IA, nunca de quem só a usa.
export const checkGovernancaIaPermission = (opts) => {
  const user = opts.user || {}
  if (user.IS_CLIENTE) return false
  if (!user.IS_GESTOR) return false
  return checkPermission(opts)
}

// Advisory Hub (spec-011, G-26) — gestor-only, mas quem barra é o BACKEND
// (403), não a rota; aqui só cliente final + feature desligada barram.
export const checkAdvisoryPermission = (opts) => {
  const user = opts.user || {}
  if (user.IS_CLIENTE) return false
  return checkPermission(opts)
}

// `acoes-ia` é a fila de ações da IA aguardando confirmação de UM
// profissional (RF6/US-9) — não é governança: qualquer assessor com CHAT_IA
// alcança a própria fila. Cliente final continua bloqueado.
export const checkAcoesIaPermission = (opts) => {
  const user = opts.user || {}
  if (user.IS_CLIENTE) return false
  return checkPermission(opts)
}

// Libera a RAIZ e a página de boas-vindas (não os 4 recursos reais, que
// continuam com `checkPermission` puro) como upsell — mesmo padrão de
// `resolveCrmRedirect`/`crm.welcome` (crm/routes.js): visível pra qualquer
// escritório sem CHAT_IA, a menos que tenha OCULTAR_CROSS_SELL. Barra
// IS_CLIENTE explicitamente (mesmo precedente de `checkApiKeysPermission`
// acima): sem o guard, cliente final de escritório sem CHAT_IA alcançava a
// welcome por URL direta e via uma vitrine de ferramenta de assessor
// (achado do review do PR #1955).
export const checkWelcomePermission = ({ user, show }) => {
  if (user && user.IS_CLIENTE) return false
  return Boolean(show[FEATURES.CHAT_IA]) || !show[FEATURES.OCULTAR_CROSS_SELL]
}

// Quem tem a flag e já dispensou a welcome (checkbox "não mostrar
// novamente") vai direto pro chat; os demais caem na página de boas-vindas
// — mesmo padrão de `resolveCrmRedirect` (crm/routes.js). Sem o
// `isWelcomeDismissed`, o checkbox marcava o dismiss mas o redirect nunca o
// lia: a welcome voltava a aparecer em toda visita, mesmo pra quem já
// marcou "não mostrar novamente" (achado do review do PR #1955). `require`
// lazy do store, mesmo padrão de `resolveCrmRedirect`.
const resolveAawzIntelligenceRedirect = () => {
  const store = require('@state/store').default
  const show = store.getters['parameters/show'] || {}
  if (show[FEATURES.CHAT_IA] && isWelcomeDismissed('aawz-intelligence')) {
    return { name: 'aawz-intelligence.chat-ia' }
  }
  return { name: 'aawz-intelligence.welcome' }
}

export default [
  {
    path: '/aawz-intelligence',
    name: 'aawz-intelligence',
    redirect: resolveAawzIntelligenceRedirect,
    meta: {
      customPermission: checkWelcomePermission,
      search: {
        title: 'AAWZ Intelligence',
        description: 'Assistente de IA para consultas e análises',
        keywords: [
          'ia',
          'inteligência',
          'assistente',
          'chat',
          'aawz intelligence',
        ],
        icon: 'ia-imagem',
        group: SEARCH_GROUPS.FERRAMENTAS,
      },
    },
    component: () =>
      lazyLoadView(
        import(
          '@modules/aawz-intelligence/views/nav-bar/aawz-intelligence-nav-bar.vue'
        )
      ),
    children: [
      {
        path: 'bem-vindo',
        name: 'aawz-intelligence.welcome',
        meta: {
          customPermission: checkWelcomePermission,
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/aawz-intelligence/views/welcome/view-aawz-intelligence-welcome.vue'
            )
          ),
      },
      {
        path: 'chat-ia',
        name: 'aawz-intelligence.chat-ia',
        meta: {
          customPermission: checkPermission,
        },
        // Casca, não o motor direto: ela é quem escuta `open-service` e abre a
        // landing do serviço citado. O motor emite e não navega — ver o
        // comentário em `view-chat-shell.vue`.
        component: () =>
          lazyLoadView(
            import('@modules/aawz-intelligence/views/chat/view-chat-shell.vue')
          ),
      },
      {
        path: 'curadoria',
        name: 'aawz-intelligence.curadoria',
        meta: {
          customPermission: checkPermission,
          search: {
            title: 'Sugestões a triar',
            description: 'Sugestões da IA aguardando aceite ou recusa do assessor',
            keywords: ['ia', 'curadoria', 'sugestões', 'triagem', 'aceitar', 'recusar'],
            group: SEARCH_GROUPS.FERRAMENTAS,
          },
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/aawz-intelligence-v2/views/curadoria/view-curadoria.vue'
            )
          ),
      },
      {
        path: 'alteracoes-ia',
        name: 'aawz-intelligence.alteracoes-ia',
        meta: {
          can_show: FEATURES.CHAT_IA,
          customPermission: checkPermission,
          search: {
            title: 'Alterações da IA',
            description: 'Histórico de alterações feitas pela IA no CRM',
            keywords: [
              'ia',
              'alterações',
              'histórico',
              'changelog',
              'inteligência',
            ],
            group: SEARCH_GROUPS.FERRAMENTAS,
          },
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/aawz-intelligence-v2/views/ia-alteracoes/view-ia-alteracoes.vue'
            )
          ),
      },
      {
        path: 'chaves-api',
        name: 'aawz-intelligence.chaves-api',
        meta: {
          customPermission: checkApiKeysPermission,
          search: {
            title: 'Chaves de API',
            description: 'Gerenciar chaves de API da IA operacional',
            keywords: ['api', 'chaves', 'tokens', 'integração', 'claude code'],
            group: SEARCH_GROUPS.FERRAMENTAS,
          },
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/aawz-intelligence-v2/views/api-keys/view-api-keys.vue'
            )
          ),
      },
      {
        // spec-002 (memoria-curada-e-perfil-do-profissional), [T019] —
        // "o que a IA sabe sobre mim/sobre os clientes/sobre o escritório".
        // Mesmo guard de Chaves de API: ferramenta do PROFISSIONAL, nunca
        // do cliente final, mesmo com CHAT_IA ligada no escritório.
        path: 'memoria-ia',
        name: 'aawz-intelligence.memoria',
        meta: {
          customPermission: checkApiKeysPermission,
          search: {
            title: 'Memória da IA',
            description: 'O que a IA sabe sobre você, seus clientes e o escritório',
            keywords: ['memória', 'ia', 'perfil', 'privacidade', 'lgpd'],
            group: SEARCH_GROUPS.FERRAMENTAS,
          },
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/aawz-intelligence-v2/views/memoria/view-memoria.vue'
            )
          ),
      },
      // spec-007-aprovacao-de-skills-por-risco ([T033]) — as 3 abas novas,
      // SEMPRE no fim (mesma ordem de `aawzIntelligenceRoutes()`, [T021]).
      {
        path: 'aprendizado-ia',
        name: 'aawz-intelligence.aprendizado-ia',
        meta: {
          customPermission: checkGovernancaIaPermission,
          search: {
            title: APRENDIZADO_TEXT.pageTitle,
            description: APRENDIZADO_TEXT.pageDescription,
            keywords: [
              'ia',
              'aprendeu',
              'publicações',
              'desfazer',
              'aprendizado',
              'timeline',
            ],
            group: SEARCH_GROUPS.FERRAMENTAS,
          },
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/aawz-intelligence-v2/views/aprendizado-ia/view-aprendizado-ia.vue'
            )
          ),
      },
      {
        path: 'skills-ia',
        name: 'aawz-intelligence.skills-ia',
        meta: {
          customPermission: checkGovernancaIaPermission,
          search: {
            title: SKILLS_TEXT.pageTitle,
            description: SKILLS_TEXT.pageDescription,
            keywords: ['ia', 'skills', 'procedimentos', 'catálogo', 'agentes'],
            group: SEARCH_GROUPS.FERRAMENTAS,
          },
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/aawz-intelligence-v2/views/skills-ia/view-skills-ia.vue'
            )
          ),
      },
      {
        path: 'acoes-ia',
        name: 'aawz-intelligence.acoes-ia',
        meta: {
          customPermission: checkAcoesIaPermission,
          search: {
            title: ACOES_TEXT.pageTitle,
            description: ACOES_TEXT.pageDescription,
            keywords: [
              'ia',
              'aguardando',
              'ações',
              'confirmação',
              'pendências',
            ],
            group: SEARCH_GROUPS.FERRAMENTAS,
          },
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/aawz-intelligence-v2/views/acoes-ia/view-acoes-ia.vue'
            )
          ),
      },
      // spec-013-knowledge-por-escritorio-e-promocao-de-decisao ([T024]) —
      // SEMPRE a ÚLTIMA aba do módulo (`aawzIntelligenceRoutes()`, [T007],
      // já reflete essa ordem). Mesmo guard de governança de
      // `aprendizado-ia`/`skills-ia`: gestor only, cliente final bloqueado.
      {
        path: 'conhecimento',
        name: 'aawz-intelligence.conhecimento',
        meta: {
          customPermission: checkGovernancaIaPermission,
          search: {
            title: CONHECIMENTO_TEXT.pageTitle,
            description: CONHECIMENTO_TEXT.pageDescription,
            keywords: [
              'ia',
              'conhecimento',
              'regras',
              'definições',
              'kpi',
              'decisão',
            ],
            group: SEARCH_GROUPS.FERRAMENTAS,
          },
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/aawz-intelligence-v2/views/conhecimento/view-conhecimento.vue'
            )
          ),
      },
      // spec-012-avaliacao-e-metricas-de-aprendizado ([T029], `contracts/
      // ui-components.md` §1) — SEMPRE a ÚLTIMA aba do módulo
      // (`aawzIntelligenceRoutes()`, já reflete essa ordem). Mesmo guard de
      // governança de `aprendizado-ia`/`skills-ia`/`conhecimento`: gestor
      // only, cliente final bloqueado.
      {
        path: 'metricas-ia',
        name: 'aawz-intelligence.metricas-ia',
        meta: {
          customPermission: checkGovernancaIaPermission,
          search: {
            title: METRICAS_TEXT.titulo,
            description: METRICAS_TEXT.subtitulo,
            keywords: ['ia', 'métricas', 'avaliação', 'evals', 'custo', 'aprendizado'],
            group: SEARCH_GROUPS.FERRAMENTAS,
          },
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/aawz-intelligence-v2/views/metricas-ia/view-metricas-ia.vue'
            )
          ),
      },
      // spec-011-advisory-engagements-do-comite ([T062], `contracts/
      // ui-components.md` §4, `design/wireframes.md` T1/T2) — SEMPRE a
      // ÚLTIMA aba do módulo (`aawzIntelligenceRoutes()` não a lista, por
      // NÃO carregar o texto de `Text.js` — ver a nota do import acima).
      // Gestor-only por regra de negócio; quem barra é o BACKEND (G-26).
      // NICE 16: rotas extraídas para `routes.advisory.js` (irmão).
      ...advisoryRoutes(checkAdvisoryPermission),
    ],
  },
  // Visão imersiva: o chat ocupando a janela inteira, sem nada da plataforma
  // em volta. Rota de TOPO, e não filha de `/aawz-intelligence`, porque o
  // componente daquela rota é o `aawz-intelligence-nav-bar.vue` — que É o
  // chrome que esta visão existe para não ter.
  //
  // ★ É a "home por IA" (raiz `/` quando `home_ia` + `chat_ia` ligadas — ver
  // `applyHomeIaRedirectGuard` em `router/index.js`). Chegou a existir uma
  // segunda versão dessa ideia (spec-001/new-ux do João, `aawz-intelligence.
  // home-ia` em `/home-ia`, reusando `view-chat.vue` com a prop `standalone`
  // pra tirar só a barra de abas mas manter sidebar/topbar) — decisão do
  // Herik em 31/08/2026, combinada com o João, foi consolidar nesta aqui (tela
  // cheia de verdade) e descontinuar a outra. O path `/ia` original virou
  // redirect pra este, logo abaixo.
  //
  // `meta.imersivo` é lido pelo `main.vue`, que troca o miolo do layout pelo
  // slot nu. Os modais globais seguem montados lá (o chat usa `$showSnack`).
  //
  // ⚠️ O nome NÃO pode começar com `aawz-intelligence` — três lugares casam por
  // PREFIXO de nome de rota, e os três quebram em silêncio:
  //   1. `collectItemsByParte` (navegacao-v2/constants/partes.js) deduplica por
  //      module key, e o item novo SUMIRIA da sidebar (1ª ocorrência vence);
  //   2. `isActiveRoute`/`NavBarNavigation` acenderiam a aba "Chat IA" junto;
  //   3. `resolveRouteHeader` devolveria o header de "Chat IA" no fallback
  //      inline do ConditionalTeleport — chrome dentro da tela imersiva.
  // Daí `ia-imersivo`, um module key próprio.
  {
    path: '/home-ia',
    name: 'ia-imersivo',
    meta: {
      imersivo: true,
      customPermission: checkImersivaPermission,
      // ★ A MESMA chave da rota por conversa, logo abaixo: é o que faz ir de
      // `/home-ia` para `/home-ia/c/<uuid>` reaproveitar a instância em vez de
      // remontar. Sem isto o `:key="$route.fullPath"` do `app.vue` destruiria o
      // `<ViewChat>` no instante em que a conversa ganha id — com o stream
      // aberto, e a resposta do primeiro turno sumiria. Ver
      // `router/chave-de-montagem.js`.
      chaveDeMontagem: ROTA_IMERSIVA,
      // Entra na busca global (Ctrl/Cmd+K). ⚠️ O `meta.search` sozinho NÃO
      // basta: o índice é gateado por título em `SIDEBAR_MODULE_TITLES`
      // (`global/utils/global-search/sidebar-modules.js`) — sem o título lá, a
      // declaração aqui é ignorada em silêncio.
      search: {
        title: 'Home IA',
        description: 'Assistente de IA em tela cheia, com o painel do escritório',
        keywords: [
          'ia',
          'driven',
          'assistente',
          'chat',
          'tela cheia',
          'imersivo',
          'home ia',
        ],
        icon: 'home-ia',
        group: SEARCH_GROUPS.FERRAMENTAS,
      },
    },
    component: telaImersivaDoChat,
  },
  // ★ UMA CONVERSA, UM ENDEREÇO (decisão do owner, 18/09/2026): *"até para que
  // cada conversa consiga ter um url direto para ela"*. Mesma tela da rota
  // acima — o que muda é o `:idConversa`, que diz QUAL conversa abrir.
  //
  // O id é um UUID v4 canônico gerado NO FRONT (`novoIdDeConversa`, em
  // `api/registro-de-conversas.js`) e mandado ao backend no corpo do turno, que
  // o adota como id da conversa. É isso que faz a URL existir antes do primeiro
  // caractere, sobreviver a um F5 e poder ser colada em outra aba.
  //
  // O id vai no PATH, nunca em query: `?conversa=` já tem dono neste app (o
  // deep link da caixa de Atendimento, `atendimento/utils/conversa-da-rota.js`)
  // e `?cliente=` já disparou `enterClient` por engano quando foi reusado.
  //
  // ⚠️ SEM `meta.search`, de propósito: a busca global indexa por ele, e
  // declará-lo aqui poria "Home IA" duas vezes no Ctrl+K — a segunda com um
  // `:idConversa` que ninguém preencheu.
  //
  // ⚠️ O nome também entra em `ROTAS_SEM_WIDGET_CHAT_IA`
  // (`global/utils/chat-ia-widget-visivel.js`): aquela lista casa por
  // IGUALDADE EXATA, e sem a linha de lá o FAB do chat volta a flutuar por
  // cima da tela cheia (o "handle órfão").
  //
  // ⚠️ [#4 do review do PR #2342] O param é RESTRITO ao formato de UUID
  // canônico (36 caracteres de hex e hífen). Sem a restrição, `/home-ia/c/`
  // seguido de QUALQUER coisa montava a tela imersiva inteira e disparava um
  // `GET /conversations/<lixo>/messages` — a tela abria vazia e o id vindo de
  // fora atravessava até o servidor. Com ela, o que não é id de conversa cai
  // no 404 da aplicação, que é onde um endereço inventado deve cair.
  //
  // ★ O formato é FATO, não palpite: o id nasce de `novoIdDeConversa()`
  // (`api/registro-de-conversas.js`, `crypto.randomUUID` com fallback que põe
  // os bits de versão e variante no lugar) e o backend o lê com `uuid_parser`
  // (`resources/resource_conversations.py`) — os dois lados já recusam o que
  // não for UUID. A classe é deliberadamente frouxa (`[0-9a-fA-F-]{36}`) em
  // vez de casar a máscara 8-4-4-4-12: o que se quer barrar é barra, ponto e
  // interrogação, e uma regex mais estreita arriscaria derrubar deep link de
  // conversa antiga por um detalhe de formato que ninguém mediu.
  {
    path: '/home-ia/c/:idConversa([0-9a-fA-F-]{36})',
    name: ROTA_IMERSIVA_CONVERSA,
    meta: {
      imersivo: true,
      customPermission: checkImersivaPermission,
      // ★ A MESMA chave da rota mãe — é ela que faz trocar de conversa NÃO
      // remontar a tela (`router/chave-de-montagem.js`). Duas rotas só
      // reaproveitam a instância se declararem o mesmo valor.
      chaveDeMontagem: ROTA_IMERSIVA,
    },
    component: telaImersivaDoChat,
  },
  // Compat: quem tinha `/ia` salvo (bookmark, link em conversa antiga) não
  // pode cair em 404 do dia pra noite só porque o path mudou pra `/home-ia`.
  {
    path: '/ia',
    redirect: '/home-ia',
  },
]
